/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * The customer page's two parts about the systems that receive reporting data
 * (docs/specs/reporting-api.md §12 "Customer page"):
 *
 * - the switch "Không gửi thông tin liên hệ cho hệ thống quản lý khách hàng", under the permission
 *   that edits the customer (the page itself needs it), with a reason (audited);
 * - the section "Dữ liệu của khách này đã gửi cho nơi nhận nào", for staff who may see the places
 *   that receive data: per place, since when it receives the customer's data and when the
 *   customer's e-mail and phone were last sent.
 */

import { ShieldOff, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { CustomerOverview } from '../../api/areas/accounts';
import type { ReportingDisclosure } from '../../api/areas/reporting';
import { Badge } from '../../components/Badge';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EmptyState, Loading } from '../../components/States';
import { useAccess } from '../../lib/access';
import { formatDate, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ReasonAction } from '../operator/shared';

type Customer = CustomerOverview['customer'];

/** "Không gửi thông tin liên hệ cho hệ thống quản lý khách hàng". */
export function ContactSharingSection({ customer, onChanged }: { customer: Customer; onChanged: () => void }) {
  const { t } = useTranslation('reporting');
  const withheld = customer.contact_sharing_withheld === true;
  const name = customer.display_name;
  return (
    <Section title={t('customer.sharingTitle')} description={t('customer.withholdHelp')}>
      <div className="flex flex-wrap items-center justify-between gap-3" data-contact-sharing={withheld ? 'withheld' : 'shared'}>
        <div className="grid gap-1 text-sm">
          <span className="font-medium">{t('customer.withhold')}</span>
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={withheld ? 'warn' : 'neutral'}>{withheld ? t('customer.on') : t('customer.off')}</Badge>
            <span className="text-muted-foreground">{withheld ? t('customer.withheldOn') : t('customer.withheldOff')}</span>
          </span>
        </div>
        <ReasonAction
          label={withheld ? t('customer.turnOff') : t('customer.turnOn')}
          icon={withheld ? ShieldCheck : ShieldOff}
          title={withheld ? t('customer.turnOffTitle', { name }) : t('customer.turnOnTitle', { name })}
          warning={withheld ? t('customer.turnOffWarning') : t('customer.withholdHelp')}
          run={(reason, opts) => api.setCustomerContactSharing(customer.id, { withheld: !withheld, reason }, opts)}
          done={(r) => (r.contact_sharing_withheld ? t('customer.turnedOn') : t('customer.turnedOff'))}
          onChanged={onChanged}
        />
      </div>
    </Section>
  );
}

/** "Dữ liệu của khách này đã gửi cho nơi nhận nào": only for staff who may see the places. */
export function DisclosureSection({ customerId }: { customerId: string }) {
  const access = useAccess();
  if (!access.has(['reporting_key.read', 'reporting_key.manage'])) return null;
  return <Disclosures customerId={customerId} />;
}

function Disclosures({ customerId }: { customerId: string }) {
  const { t } = useTranslation('reporting');
  const result = useApi((signal) => api.getReportingDisclosures(customerId, { signal }), [customerId]);
  const columns: Column<ReportingDisclosure>[] = [
    { key: 'place', header: t('customer.col.place'), cell: (d) => <span className="font-medium">{d.consumer_name}</span> },
    {
      key: 'since',
      header: t('customer.col.since'),
      className: 'whitespace-nowrap',
      cell: (d) => (d.first_served_at ? <span title={formatDateTime(d.first_served_at)}>{formatDate(d.first_served_at)}</span> : <span className="text-muted-foreground">{t('customer.notYet')}</span>),
    },
    {
      key: 'contacts',
      header: t('customer.col.contacts'),
      cell: (d) =>
        d.contact_disclosures > 0 && d.last_contact_disclosure_at ? (
          <span title={formatDateTime(d.last_contact_disclosure_at)}>{t('customer.contactsSent', { times: d.contact_disclosures, when: formatRelative(d.last_contact_disclosure_at) })}</span>
        ) : (
          <span className="text-muted-foreground">{t('customer.never')}</span>
        ),
    },
  ];
  let body;
  if (result.status === 'loading') body = <Loading label={t('customer.disclosuresTitle')} rows={2} />;
  else if (result.status === 'error') body = <DataError error={result.error} onRetry={result.reload} title={t('customer.disclosuresFailed')} />;
  else body = <DataTable columns={columns} rows={result.data.items} rowKey={(d) => d.consumer_id} empty={<EmptyState title={t('customer.noPlaces')} />} />;
  return (
    <Section flush title={t('customer.disclosuresTitle')} description={t('customer.disclosuresHelp')}>
      <div data-reporting-disclosures>{body}</div>
    </Section>
  );
}
